import React, { useState, useEffect } from 'react';
import './Reports.css';
import axios from 'axios';
import { API_CONFIG } from '../../../config/api.config';
import { authService } from '../../../services/auth';
import { getSystemStats, getTopClients, getMonthlyUsage } from '../../../services/api';

// Interface para tipagem dos dados do relatório
interface ReportData {
  summary: {
    totalClients: number;
    activeClients: number;
    totalCreditsUsed: number;
    totalCreditsAdded: number;
    totalConsultations: number;
    totalPidkeyChecks: number;
    totalOcrRequests: number;
  };
  topClients: {
    id: string;
    name: string;
    consultations: number;
    credits: number;
    revenue: number; // Receita em reais
  }[];
  monthlyUsage: {
    month: string;
    consultations: number;
    pidkey: number;
    ocr: number;
    credits: number; // Total de créditos
    revenue: number; // Receita em reais
  }[];
}

// Função auxiliar para formatar data no formato YYYY-MM-DD
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Obter primeiro dia do mês atual formatado
const getFirstDayOfCurrentMonth = (): string => {
  const date = new Date();
  date.setDate(1); // Define para o primeiro dia do mês
  return formatDateForInput(date);
};

// Obter último dia do mês atual formatado
const getLastDayOfCurrentMonth = (): string => {
  const date = new Date();
  // Define para o primeiro dia do próximo mês e subtrai 1 dia
  date.setMonth(date.getMonth() + 1, 0);
  return formatDateForInput(date);
};

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{start: string, end: string}>({
    start: getFirstDayOfCurrentMonth(),
    end: getLastDayOfCurrentMonth()
  });

  // Função para buscar dados do relatório da API
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Formatar datas para a API
      const start = dateRange.start ? dateRange.start.split('T')[0] : '2023-01-01';
      const end = dateRange.end ? dateRange.end.split('T')[0] : '2025-12-31';
      // Chamadas em paralelo para melhor performance usando as funções da API
      const [statsResponse, topClientsResponse, monthlyUsageResponse] = await Promise.all([
        getSystemStats(start, end),
        getTopClients(start, end),
        getMonthlyUsage(start, end)
      ]);

      const topClients = topClientsResponse.success && topClientsResponse.topClients && 
                        Array.isArray(topClientsResponse.topClients) ? 
                        topClientsResponse.topClients : [];
      
      // Verificar se monthlyUsageResponse.monthlyUsage existe e é array
      const monthlyUsage = monthlyUsageResponse.success && monthlyUsageResponse.monthlyUsage && 
                          Array.isArray(monthlyUsageResponse.monthlyUsage) ? 
                          monthlyUsageResponse.monthlyUsage : [];
      
      // Montar objeto de dados do relatório com os dados reais
      const realData: ReportData = {
        summary: {
          totalClients: statsResponse.data?.totals?.total_clients || 0,
          activeClients: statsResponse.data?.totals?.active_clients || 0,
          totalCreditsUsed: statsResponse.data?.totals?.total_credits_spent || 0,
          totalCreditsAdded: statsResponse.data?.creditsAdded || 0,
          totalConsultations: statsResponse.data?.totals?.total_consultations || 0,
          totalPidkeyChecks: statsResponse.data?.totals?.total_pidkey_checks || 0,
          totalOcrRequests: statsResponse.data?.totals?.total_ocr_requests || 0
        },
        topClients: topClients.map((client: any) => ({
          id: client.id || `id-${Math.random()}`,
          name: client.name || 'Cliente sem nome',
          consultations: Number(client.totalRequests) || 0,
          credits: Number(client.totalCredits) || 0,
          revenue: (Number(client.totalCredits) || 0) * 5 // Cada crédito custa R$ 5
        })),
        monthlyUsage: monthlyUsage.map((month: any) => ({
          month: month.month || 'Mês desconhecido',
          consultations: Number(month.GetCID) || 0,
          pidkey: Number(month.PIDKey) || 0,
          ocr: Number(month.OCR) || 0,
          credits: Number(month.totalCredits) || 0,
          revenue: (Number(month.totalCredits) || 0) * 5 // Cada crédito custa R$ 5
        }))
      };
      
      // console.log('Dados do relatório montados:', realData);
      setReportData(realData);
    } catch (error) {
      console.error('Erro ao buscar dados do relatório:', error);
      setError('Não foi possível carregar os dados do relatório. Por favor, tente novamente mais tarde.');
      
      // Utilizar dados vazios em caso de erro, não dados fictícios
      setReportData({
        summary: {
          totalClients: 0,
          activeClients: 0,
          totalCreditsUsed: 0,
          totalCreditsAdded: 0,
          totalConsultations: 0,
          totalPidkeyChecks: 0,
          totalOcrRequests: 0
        },
        topClients: [],
        monthlyUsage: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    fetchReportData();
  };
  
  // Função para exportar relatório em CSV
  const handleExportCSV = () => {
    if (!reportData) return;
    
    // Criar cabeçalho do CSV
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Adicionar resumo
    csvContent += "Resumo\n";
    csvContent += "Total de Clientes," + reportData.summary.totalClients + "\n";
    csvContent += "Clientes Ativos," + reportData.summary.activeClients + "\n";
    csvContent += "Créditos Adicionados," + reportData.summary.totalCreditsAdded.toFixed(2) + ` (R$ ${(reportData.summary.totalCreditsAdded * 5).toFixed(2)})\n`;
    csvContent += "Créditos Utilizados," + reportData.summary.totalCreditsUsed.toFixed(2) + ` (R$ ${(reportData.summary.totalCreditsUsed * 5).toFixed(2)})\n`;
    csvContent += "Consultas GetCID," + reportData.summary.totalConsultations + "\n";
    csvContent += "Verificações PidKey," + reportData.summary.totalPidkeyChecks + "\n";
    csvContent += "Consultas OCR," + reportData.summary.totalOcrRequests + "\n\n";
    
    // Adicionar top clientes
    csvContent += "Top Clientes por Consumo\n";
    csvContent += "Cliente,Consultas,Créditos Utilizados,Receita (R$)\n";
    reportData.topClients.forEach(client => {
      csvContent += `${client.name},${client.consultations},${client.credits.toFixed(2)},R$ ${(client.credits * 5).toFixed(2)}\n`;
    });
    csvContent += "\n";
    
    // Adicionar uso mensal
    csvContent += "Uso Mensal\n";
    csvContent += "Mês,GetCID,PidKey,OCR,Créditos,Receita (R$)\n";
    reportData.monthlyUsage.forEach(month => {
      csvContent += `${month.month},${month.consultations},${month.pidkey},${month.ocr},${month.credits.toFixed(2)},R$ ${month.revenue.toFixed(2)}\n`;
    });
    
    // Criar link para download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `relatorio_${dateRange.start}_a_${dateRange.end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="loading">Carregando relatórios...</div>;
  }

  if (error && !reportData) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!reportData) {
    return <div className="error-message">Não foi possível carregar os dados do relatório.</div>;
  }

  return (
    <div className="reports-container">
      <h2>Relatórios do Sistema</h2>
      
      <div className="filter-section">
        <form onSubmit={handleFilter}>
          <div className="filter-inputs">
            <div className="input-group">
              <label>Data Inicial:</label>
              <input
                type="date"
                name="start"
                value={dateRange.start}
                onChange={handleDateRangeChange}
              />
            </div>
            <div className="input-group">
              <label>Data Final:</label>
              <input
                type="date"
                name="end"
                value={dateRange.end}
                onChange={handleDateRangeChange}
              />
            </div>
            <button type="submit" className="btn-filter">Filtrar</button>
          </div>
        </form>
      </div>

      <div className="dashboard-grid">
        <div className="summary-stats">
          <div className="stat-card">
            <h3>Clientes</h3>
            <div className="stat-values">
              <div className="stat-item">
                <span className="stat-label">Total:</span>
                <span className="stat-value">{reportData.summary.totalClients}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Ativos:</span>
                <span className="stat-value">{reportData.summary.activeClients}</span>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Consultas</h3>
            <div className="stat-values">
              <div className="stat-item">
                <span className="stat-label">GetCID:</span>
                <span className="stat-value">{reportData.summary.totalConsultations}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">PidKey:</span>
                <span className="stat-value">{reportData.summary.totalPidkeyChecks}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">OCR:</span>
                <span className="stat-value">{reportData.summary.totalOcrRequests}</span>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Créditos</h3>
            <div className="stat-values">
              <div className="stat-item">
                <span className="stat-label">Adicionados:</span>
                <span className="stat-value">{reportData.summary.totalCreditsAdded.toFixed(2)} <small>(R$ {(reportData.summary.totalCreditsAdded * 5).toFixed(2)})</small></span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Utilizados:</span>
                <span className="stat-value">{reportData.summary.totalCreditsUsed.toFixed(2)} <small>(R$ {(reportData.summary.totalCreditsUsed * 5).toFixed(2)})</small></span>
              </div>
            </div>
          </div>
        </div>

        <div className="top-clients">
          <h3>Top Clientes por Consumo</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Consultas</th>
                <th>Créditos Utilizados</th>
                <th>Receita (R$)</th>
              </tr>
            </thead>
            <tbody>
              {reportData.topClients.map((client) => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td>{client.consultations}</td>
                  <td>{client.credits.toFixed(2)}</td>
                  <td>R$ {(client.credits * 5).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="monthly-usage">
          <h3>Uso Mensal</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Mês</th>
                <th>GetCID</th>
                <th>PidKey</th>
                <th>OCR</th>
                <th>Créditos</th>
                <th>Receita (R$)</th>
              </tr>
            </thead>
            <tbody>
              {reportData.monthlyUsage.map((month, index) => (
                <tr key={index}>
                  <td>{month.month}</td>
                  <td>{month.consultations}</td>
                  <td>{month.pidkey}</td>
                  <td>{month.ocr}</td>
                  <td>{month.credits.toFixed(2)}</td>
                  <td>R$ {month.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="export-section">
        <button className="btn-export" onClick={handleExportCSV}>Exportar Relatório (CSV)</button>
        <button className="btn-export">Exportar Relatório (PDF)</button>
      </div>
    </div>
  );
};

export default Reports; 